import { memo, useCallback, useContext, useMemo } from "react";
import "../../css/GlobalStyles.css";
import { useSelector, useDispatch } from "react-redux";
import { LoginState, getAccountState } from "../../redux/slices/accountSlice";
import { Colors } from "../../utils/colors";
import { MARKIT_CONTACT_US, MARKITAI } from "@markit/common.utils";
import { useNavigate } from "../../hooks/useNavigate";
import { DataLoaders } from "../../redux/slices/dataSlice";
import RectangleButton from "../../components/Buttons/RectangleButton";
import { Icon } from "@iconify/react";
import { NavigationId } from "../../navigation/AppParamList";
import LandingPagesWrapper from "../../components/LandingPage/LandingPagesWrapper";
import { FloatingBubbles } from "../../components/AnimationContainers/FloatingBubbles";
import {
  AttachLinksAndImages,
  AutoReplies,
  NeverMissAText,
  ReachTheRightPeopleDesktop,
  ReachTheRightPeopleMobile,
  ScheduleAndForget,
  TextThousandsInSecondsDesktop,
  TextThousandsInSecondsMobile,
  WelcomeFlows,
} from "../../assets/LandingPage/TextingPage/textingPageIndex";
import iMessageOnSteroids from "../../assets/LandingPage/iMessageOnSteroids.png";
import { HorizontalDivider } from "../../components/Dividers/HorizontalDivider";
import { useSlideUpAnimation } from "../../hooks/animationHooks/useSlideUpAnimation";
import { useWindowResize } from "../../hooks/useWindowResize";
import { checkIsDesktop } from "../../utils/screenResponsivenessUtils";
import LabelButton from "../../components/Buttons/LabelButton";
import { MixpanelContext } from "../../context/AnalyticsService";
import LandingPageDividedSection from "../../components/LandingPage/LandingPageDividedSection";
import TextingThatFeelsHumanFold from "../../components/LandingPage/TextingThatFeelsHumanFold";
import YourAudienceYourWay from "../../assets/LandingPage/YourAudienceYourWay.png";
import YourAudienceYourWayMobile from "../../assets/LandingPage/YourAudienceYourWayMobile.png";
import CollectDataWithForms from "../../assets/LandingPage/CollectDataWithForms.png";
import ShareMarkitNumber from "../../assets/LandingPage/ShareMarkitNumber.png";
import { FoldHeaderText } from "../../components/LandingPage/SubComponents/FoldHeaderText";

const TextingProductPage = memo(function TextingProductPageFn() {
  const { account } = useSelector(getAccountState);
  const { accountData, loggedIn } = account;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const mixpanel = useContext(MixpanelContext);

  useSlideUpAnimation();
  const windowWidth = useWindowResize();

  const isDesktopSize = useMemo(
    () => checkIsDesktop(windowWidth),
    [windowWidth]
  );

  const styles = {
    button: {
      padding: "12px 16px",
      borderRadius: 10,
      width: "fit-content",
    },

    foldContainer: {
      paddingBlock: isDesktopSize ? 80 : 60,
      backgroundImage: "linear-gradient(to bottom, #141516 0%, #000000 31%)",
    },

    mainTitle: {
      fontSize: isDesktopSize ? 80 : 60,
      fontWeight: 500,
      paddingBlock: 24,
      letterSpacing: "-1px",
      backgroundImage: "linear-gradient(to bottom, #FFFFFF 40%, #787878 100%)",
      WebkitBackgroundClip: "text",
      WebkitTextFillColor: "transparent",
      backgroundClip: "text",
    },

    headerLabel: {
      fontSize: isDesktopSize ? 16 : 14,
      fontWeight: 500,
    },

    headerSubtext: {
      fontSize: 18,
      fontWeight: 500,
      color: Colors.GRAY1,
      letterSpacing: "-0.5px",
      lineHeight: "24px",
    },

    headerText: {
      fontSize: isDesktopSize ? 64 : 48,
      fontWeight: 500,
      letterSpacing: "-0.5px",
      color: Colors.WHITE,
    },

    largeImage: {
      width: "100%",
      height: "auto",
    },

    safeView: {
      paddingInline: 20,
    },
  };

  const getStartedOnPress = useCallback(
    (btnSource: string) => {
      if (mixpanel) {
        mixpanel.track("Website: Home Pressed Get Started", {
          distinct_id: accountData.uid,
          is_logged_in: loggedIn === LoginState.LOGGED_IN,
          btn_source: btnSource,
        });
      }
      if (loggedIn === LoginState.LOGGED_IN) {
        dispatch(DataLoaders.user(accountData.uid));
        navigate(`/u/${accountData.username}`, {
          state: { username: accountData.username },
        });
      } else {
        navigate(NavigationId.WELCOME);
      }
    },
    [
      accountData.uid,
      accountData.username,
      dispatch,
      loggedIn,
      navigate,
      mixpanel,
    ]
  );

  const supportOnPress = useCallback(
    (btnSource: string) => {
      if (mixpanel) {
        mixpanel.track("Website: Home Pressed Contact Us", {
          distinct_id: accountData.uid,
          is_logged_in: loggedIn === LoginState.LOGGED_IN,
          btn_source: btnSource,
        });
      }
      window.open(MARKIT_CONTACT_US);
    },
    [accountData.uid, loggedIn, mixpanel]
  );

  const renderFoldHeaderLabel = useCallback(
    (label: string, iconName: string, color: string) => (
      <div
        className="AlignedRow"
        style={{ gap: 10, paddingBottom: isDesktopSize ? 10 : 7 }}
      >
        <Icon icon={iconName} height={14} style={{ color }} />
        <span style={{ ...styles.headerLabel, color }}>{label}</span>
      </div>
    ),
    [styles.headerLabel, isDesktopSize]
  );

  return (
    <LandingPagesWrapper
      helmetData={{
        title: "Texting Features | Markit AI",
        metaContent:
          "Publish newsletters and share updates with your text subscribers, all from your own dedicated number and contact card.",
        link: MARKITAI + "/texting",
      }}
      noRedirect
    >
      <div
        className="AlignedColumn Centering"
        style={{
          paddingBlock: 100,
          color: Colors.WHITE,
          height: 608,
          ...styles.safeView,
        }}
      >
        <FloatingBubbles />
        <LabelButton
          text="Mass Text Blasting"
          color={Colors.BLUE5}
          backgroundColor={Colors.BLUE5 + "1a"}
          icon={
            <Icon
              icon="ion:paper-plane"
              height={14}
              style={{ color: Colors.BLUE5 }}
            />
          }
          borderColor={Colors.BLUE5}
          containerStyles={{ fontSize: 16, fontWeight: 500 }}
        />
        <h1 style={{ ...styles.mainTitle, textAlign: "center", zIndex: 100 }}>
          Next level texting.
        </h1>
        <p style={{ ...styles.headerSubtext, textAlign: "center" }}>
          Get the most out of your audience with {isDesktopSize ? null : <br />}
          perfectly timed campaigns, {isDesktopSize ? <br /> : null}{" "}
          automations, {isDesktopSize ? null : <br />}
          two-way texting, and more.
        </p>
      </div>
      {/* Fold 2 */}
      <div style={{ ...styles.safeView, ...styles.foldContainer }}>
        <div style={{ maxWidth: 1152, margin: "0 auto" }}>
          <div className="ColumnNormal" style={{ gap: isDesktopSize ? 0 : 50 }}>
            <div className="ColumnNormal">
              {renderFoldHeaderLabel(
                "SMS Campaigns",
                "ion:paper-plane",
                Colors.BLUE5
              )}
              <div className="ColumnNormal" style={{ gap: 40 }}>
                <div
                  className="ColumnNormal"
                  style={{ maxWidth: isDesktopSize ? 633 : "auto", gap: 24 }}
                >
                  <h2 style={styles.headerText}>
                    Reach the right {isDesktopSize ? null : <br />}people{" "}
                    {isDesktopSize ? <br /> : null}
                    at the {isDesktopSize ? null : <br />}perfect time.
                  </h2>
                  <p style={{ ...styles.headerSubtext }}>
                    Create, send, and schedule hundreds of targeted text
                    campaigns.{isDesktopSize ? <br /> : " "}
                    View analytics and manage all of your texts on desktop and
                    mobile.
                  </p>
                </div>
                <div
                  className="AlignedRow"
                  style={{ gap: isDesktopSize ? 14 : 10 }}
                >
                  <RectangleButton
                    buttonLabel="Start Texting"
                    onPress={() => getStartedOnPress("Texting Page First Fold")}
                    altColor={Colors.WHITE}
                    altTextColor={Colors.BLACK}
                    iconRight={
                      <Icon
                        icon="ion:chevron-forward"
                        height={16}
                        style={{ color: Colors.BLACK }}
                      />
                    }
                    containerStyles={styles.button}
                  />
                  <RectangleButton
                    buttonLabel="Book a Demo"
                    onPress={() => supportOnPress("Book a Demo Texting Page")}
                    altColor={Colors.BLACK}
                    altTextColor={Colors.WHITE}
                    containerStyles={{
                      ...styles.button,
                      border: `0.5px solid ${Colors.BLACK1}`,
                    }}
                  />
                </div>
              </div>
            </div>
            <img
              src={
                isDesktopSize
                  ? ReachTheRightPeopleDesktop
                  : ReachTheRightPeopleMobile
              }
              alt="Campaigns dashboard"
              className="slide-up"
              style={{
                ...styles.largeImage,
                objectFit: "contain",
                maxWidth: 1152,
              }}
            />
          </div>
        </div>
      </div>
      {isDesktopSize ? (
        <HorizontalDivider
          altWidth={0.5}
          altColor={Colors.BLACK1}
          containerStyles={{ maxWidth: 1152, margin: "0 auto" }}
        />
      ) : null}
      {/* Fold 3 */}
      <div
        className="ColumnNormal"
        style={{
          ...styles.safeView,
          backgroundColor: Colors.BLACK,
          paddingTop: isDesktopSize ? 80 : 0,
          paddingBottom: isDesktopSize ? 80 : 60,
          maxWidth: 1152,
          margin: "0 auto",
          gap: isDesktopSize ? 60 : 40,
        }}
      >
        <div className="ColumnNormal">
          <div
            className={isDesktopSize ? "AlignedRowSpaced" : "ColumnNormal"}
            style={{ gap: 30 }}
          >
            <h2 style={styles.headerText}>
              Text Thousands
              <br />
              In Seconds.
            </h2>
            <p
              style={{
                ...styles.headerSubtext,
                maxWidth: isDesktopSize ? 390 : "auto",
              }}
            >
              Powerful and intuitive text drafting tools enable you to send
              personalized texts to thousands of people in seconds, with zero
              effort.
            </p>
          </div>
        </div>
        <img
          src={
            isDesktopSize
              ? TextThousandsInSecondsDesktop
              : TextThousandsInSecondsMobile
          }
          alt="Mass text mobile annotated"
          className="slide-up"
          style={{
            ...styles.largeImage,
            objectFit: "contain",
            maxWidth: 1152,
          }}
        />
        <LandingPageDividedSection
          isDesktopSize={isDesktopSize}
          leftSection={{
            header: "Schedule and forget",
            subtext:
              "Schedule your texts to send in the future. We give built in recommendations for scheduled texts when managing events and more.",
            image: ScheduleAndForget,
            imageWidth: isDesktopSize ? 411 : undefined,
          }}
          rightSection={{
            header: "Attach links and images",
            subtext:
              "Attach images to your texts and send discount ticket links, membership links, and more.",
            image: AttachLinksAndImages,
          }}
          verticalDividerHeight={730}
        />
      </div>
      {/* Fold 4 */}
      <div
        className={isDesktopSize ? "AlignedColumn" : "ColumnNormal"}
        style={{
          ...styles.safeView,
          ...styles.foldContainer,
          gap: isDesktopSize ? 60 : 50,
        }}
      >
        <div
          className={isDesktopSize ? "AlignedColumn" : "ColumnNormal"}
          style={{ gap: 24 }}
        >
          <LabelButton
            text="Two-Way Texting"
            color={Colors.BLUE5}
            backgroundColor={Colors.BLUE5 + "1a"}
            icon={
              <Icon
                icon="ion:chatbubbles"
                height={14}
                style={{ color: Colors.BLUE5 }}
              />
            }
            borderColor={Colors.BLUE5}
            containerStyles={{ fontSize: 16, fontWeight: 500 }}
          />
          <h2 style={styles.headerText}>
            Conversations{isDesktopSize ? " " : <br />}that convert
          </h2>
          <p
            style={{
              ...styles.headerSubtext,
              textAlign: isDesktopSize ? "center" : "start",
            }}
          >
            Create your personal authentic channel to connect with your top
            fans.{isDesktopSize ? <br /> : " "}People can reply like
            they&apos;re chatting with a friend.
          </p>
        </div>
        <img
          src={iMessageOnSteroids}
          alt="Two way texting panel"
          className="slide-up"
          style={{
            ...styles.largeImage,
            objectFit: "contain",
            maxWidth: 900,
          }}
        />
      </div>
      {/* Fold 5 */}
      <div
        className={isDesktopSize ? "AlignedColumn" : "ColumnNormal"}
        style={{
          ...styles.foldContainer,
          ...styles.safeView,
          gap: isDesktopSize ? 60 : 40,
        }}
      >
        <div
          className="ColumnNormal"
          style={{
            width: isDesktopSize ? 1152 : "100%",
            height: isDesktopSize ? 456 : 319,
            backgroundImage: `url(${
              isDesktopSize ? YourAudienceYourWay : YourAudienceYourWayMobile
            })`,
            backgroundSize: "contain",
            backgroundRepeat: "no-repeat",
            backgroundPosition: "center",
          }}
        >
          {renderFoldHeaderLabel(
            "Built for Growth",
            "ion:trending-up",
            Colors.GREEN2
          )}
          <div className="ColumnNormal" style={{ gap: 40 }}>
            <div className="ColumnNormal" style={{ gap: 24 }}>
              <FoldHeaderText isDesktopSize={isDesktopSize}>
                {isDesktopSize ? (
                  <>Your Audience, your way.</>
                ) : (
                  <>
                    Your
                    <br />
                    Audience,
                    <br />
                    your way.
                  </>
                )}
              </FoldHeaderText>
              {isDesktopSize ? (
                <p className="landingPageHeaderSubtext">
                  <span style={{ color: Colors.WHITE }}>
                    Every creator has a unique connection with their followers.
                  </span>
                  {isDesktopSize ? <br /> : " "}
                  Markit has a suite of growth tools from sign up forms to
                  tracking links{isDesktopSize ? <br /> : " "}that enable
                  creators to grow with the channel that suits them best.
                </p>
              ) : null}
            </div>
            {isDesktopSize ? (
              <RectangleButton
                buttonLabel="Get Started"
                onPress={() => getStartedOnPress("Grow with Markit")}
                altColor={Colors.WHITE}
                altTextColor={Colors.BLACK}
                iconRight={
                  <Icon
                    icon="ion:chevron-forward"
                    height={16}
                    style={{ color: Colors.BLACK }}
                  />
                }
                containerStyles={styles.button}
              />
            ) : null}
          </div>
        </div>
        {!isDesktopSize ? (
          <p className="landingPageHeaderSubtext">
            <span style={{ color: Colors.WHITE }}>
              Every creator has a unique connection with their followers.
            </span>
            {isDesktopSize ? <br /> : " "}
            Markit has a suite of growth tools from sign up forms to tracking
            links{isDesktopSize ? <br /> : " "}that enable creators to grow with
            the channel that suits them best.
          </p>
        ) : null}
        <LandingPageDividedSection
          isDesktopSize={isDesktopSize}
          leftSection={{
            header: "Collect data with forms",
            subtext:
              "Forms can be used to capture phone numbers, additional data, segment, and your audience, and grow your audience.",
            image: CollectDataWithForms,
          }}
          rightSection={{
            header: `Share your${isDesktopSize ? " " : "\n"}Markit number`,
            subtext:
              "Share your unique Markit number to social media and on your stories and posts to grow your textable following.",
            image: ShareMarkitNumber,
          }}
          verticalDividerHeight={676}
        />
      </div>
      {/* Fold 6 */}
      <div
        className={isDesktopSize ? "AlignedColumn" : "ColumnNormal"}
        style={{
          ...styles.safeView,
          ...styles.foldContainer,
          gap: 60,
        }}
      >
        <div className="ColumnNormal" style={{ maxWidth: 1152, gap: 60 }}>
          <div
            className={isDesktopSize ? "RowNormal" : "ColumnNormal"}
            style={{ gap: isDesktopSize ? 80 : 20 }}
          >
            <div className="ColumnNormal">
              {renderFoldHeaderLabel(
                "Automations",
                "fa-solid:network-wired",
                Colors.GREEN2
              )}
              <div className="ColumnNormal" style={{ gap: 30 }}>
                <h2 style={styles.headerText}>
                  Never skip a beat
                  <br />
                  with automations
                </h2>
                <p style={{ ...styles.headerSubtext, maxWidth: 380 }}>
                  Send timely texts with smart triggers & tracking. Perfect for
                  events, capturing data, and tracking form submissions—stay
                  connected effortlessly.
                </p>
                <div
                  className="AlignedRow"
                  style={{ gap: isDesktopSize ? 14 : 10 }}
                >
                  <RectangleButton
                    buttonLabel="Get Started"
                    onPress={() => getStartedOnPress("Texting Page Last Fold")}
                    altColor={Colors.WHITE}
                    altTextColor={Colors.BLACK}
                    iconRight={
                      <Icon
                        icon="ion:chevron-forward"
                        height={16}
                        style={{ color: Colors.BLACK }}
                      />
                    }
                    containerStyles={styles.button}
                  />
                </div>
              </div>
            </div>
            <img
              src={NeverMissAText}
              alt="Automation item"
              className="slide-up"
              style={{
                ...styles.largeImage,
                objectFit: "contain",
                maxWidth: 587,
              }}
            />
          </div>
          <LandingPageDividedSection
            isDesktopSize={isDesktopSize}
            leftSection={{
              header: "Automated text flows",
              subtext:
                "Save time and automate responses to make sure you send an immediate text when people follow you.",
              image: WelcomeFlows,
            }}
            rightSection={{
              header: "Set trigger replies",
              subtext:
                "Customize the message people get when they directly text your phone number, as well as auto replies, keywords, and actions.",
              image: AutoReplies,
              imageWidth: isDesktopSize ? 416 : undefined,
            }}
            verticalDividerHeight={765}
          />
        </div>
      </div>
      {/* Fold 6 */}
      <TextingThatFeelsHumanFold
        isDesktopSize={isDesktopSize}
        getStartedOnPress={getStartedOnPress}
      />
    </LandingPagesWrapper>
  );
});

export default TextingProductPage;
